/**
 * Runtime colour values.
 *
 * The CSS tokens in index.css are the source of truth for anything rendered
 * through a class name. This file exists for the handful of places that need a
 * colour as a JavaScript *value* — canvas-confetti, chart libraries, native
 * bridge calls — where a Tailwind class cannot reach.
 *
 * It is the ONLY file allowed to contain a colour literal outside index.css
 * (see docs/design/design.md §8). If you are about to type a hex anywhere else, you
 * want a token instead.
 *
 * Keep in sync with the @theme block in index.css.
 */
export const COLORS = {
    /** Brand green. Fill only — it does not clear 4.5:1 as text on white. */
    primary: '#58CC02',
    /** Hover state for primary surfaces. */
    primaryHover: '#4CAD02',
    /** The bottom edge of a raised button. */
    primaryShadow: '#46A302',
    /** Soft green used for background glows and gradients. */
    primarySoft: '#5BBF00',
    /** Green that is legible as text on white. */
    primaryText: '#3D7C02',

    error: '#FF4B4B',
    warning: '#FFB800',
    warningBg: '#FFF8E5',
    info: '#2B70C9',
    accent: '#CE82FF',
    gold: '#FFD200',

    /** Amber score state on the results screen, and its raised bottom edge. */
    amber: '#F59E0B',
    amberShadow: '#B45309',
    /** Confetti greens and the warm accent that partners gold. */
    greenLight: '#90EE90',
    greenPale: '#98FB98',
    orange: '#FFA500',
} as const;

/**
 * The colours the platform paints outside the WebView — the Telegram header and
 * background, and the browser / PWA `theme-color` in dark mode. theme/theme.ts
 * applies them; the matching CSS roles are `--color-ground-session` and
 * `--color-ground` in index.css (docs/design/dark-mode-plan.md §4).
 */
export const CHROME = {
    light: {
        /** Eggshell — what the Telegram header has always been. */
        telegramHeader: '#F1E9DA',
    },
    dark: {
        /** `--color-ground-session` in dark, the one dark page ground. */
        telegramHeader: '#141A17',
        /** `--color-ground` in dark. */
        ground: '#141A17',
    },
} as const;

/**
 * The practice-history chart. Chart.js paints a canvas, which no class reaches,
 * so it reads these through `useResolvedTheme()`. Light is what it always drew;
 * dark takes the roles' dark values in index.css.
 */
export const CHART = {
    light: {
        /** A day with no practice. */
        emptyBar: '#E5E5E5',
        /** The day labels. */
        tick: '#777777',
    },
    dark: {
        /** `--color-line-strong` in dark. */
        emptyBar: '#364139',
        /** `--color-ink-faint` in dark. */
        tick: '#7F8982',
    },
    /** The tooltip: the same in both themes. */
    tooltip: '#1CB0F6',
} as const;

/** Palette for the celebration confetti burst. */
export const CONFETTI_COLORS = [COLORS.primary, COLORS.greenLight, COLORS.greenPale] as const;

/** Confetti for a high score — adds gold and the warm orange. */
export const CONFETTI_COLORS_GOLD = [
    COLORS.primary, COLORS.greenLight, COLORS.greenPale, COLORS.gold, COLORS.orange,
] as const;

/** Confetti for passing a mock exam — greens plus gold, no pale tints. */
export const CONFETTI_COLORS_PASS = [
    '#22C55E', '#10B981', '#059669', '#FFD700', COLORS.orange,
] as const;
